import { motion } from "framer-motion";

interface MovingBackgroundProps {
  variant?: "orbs" | "mesh" | "particles" | "waves" | "grid";
}

const MovingBackground = ({ variant = "orbs" }: MovingBackgroundProps) => {
  if (variant === "orbs") {
    return (
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full opacity-[0.08]"
          style={{ background: "radial-gradient(circle, hsl(var(--primary)), transparent 70%)", top: "-10%", right: "-10%" }}
          animate={{ x: [0, 40, -20, 0], y: [0, -30, 20, 0], scale: [1, 1.15, 0.95, 1] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute w-[500px] h-[500px] rounded-full opacity-[0.06]"
          style={{ background: "radial-gradient(circle, hsl(var(--primary-glow)), transparent 70%)", bottom: "-15%", left: "-5%" }}
          animate={{ x: [0, -30, 40, 0], y: [0, 40, -20, 0], scale: [1, 0.9, 1.1, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute w-[300px] h-[300px] rounded-full opacity-[0.05]"
          style={{ background: "radial-gradient(circle, hsl(var(--success)), transparent 70%)", top: "40%", left: "30%" }}
          animate={{ x: [0, 50, -30, 0], y: [0, -40, 30, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
    );
  }

  if (variant === "mesh") {
    return (
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
          animate={{ backgroundPosition: ["0px 0px", "60px 60px"] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute w-[800px] h-[800px] rounded-full opacity-[0.06]"
          style={{ background: "radial-gradient(circle, hsl(var(--primary)), transparent 60%)", top: "50%", left: "50%", marginTop: "-400px", marginLeft: "-400px" }}
          animate={{ scale: [1, 1.3, 1], rotate: [0, 180, 360] }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  if (variant === "particles") {
    return (
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full"
            style={{
              background: i % 3 === 0 ? "hsl(var(--primary))" : i % 3 === 1 ? "hsl(var(--primary-glow))" : "hsl(var(--success))",
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.3 + Math.random() * 0.4,
            }}
            animate={{
              y: [0, -30 - Math.random() * 50, 0],
              x: [0, (Math.random() - 0.5) * 60, 0],
              opacity: [0.2, 0.6, 0.2],
              scale: [1, 1.5 + Math.random(), 1],
            }}
            transition={{ duration: 4 + Math.random() * 6, repeat: Infinity, ease: "easeInOut", delay: Math.random() * 3 }}
          />
        ))}
      </div>
    );
  }

  if (variant === "waves") {
    return (
      <div className="absolute inset-0 overflow-hidden">
        {[0, 1, 2].map(i => (
          <motion.div
            key={i}
            className="absolute w-[200%] h-[200px]"
            style={{
              bottom: `${i * 8 - 10}%`,
              left: "-50%",
              background: `linear-gradient(90deg, transparent 0%, hsl(var(--primary) / ${0.03 + i * 0.02}) 25%, hsl(var(--primary-glow) / ${0.04 + i * 0.01}) 50%, hsl(var(--primary) / ${0.03 + i * 0.02}) 75%, transparent 100%)`,
              borderRadius: "40%",
            }}
            animate={{ x: ["-25%", "0%", "-25%"], rotate: [0, 2, 0] }}
            transition={{ duration: 8 + i * 4, repeat: Infinity, ease: "easeInOut" }}
          />
        ))}
      </div>
    );
  }

  // grid
  return (
    <div className="absolute inset-0 overflow-hidden">
      <motion.div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(hsl(var(--primary)) 2px, transparent 2px),
            linear-gradient(90deg, hsl(var(--primary)) 2px, transparent 2px)
          `,
          backgroundSize: "80px 80px",
        }}
        animate={{ opacity: [0.03, 0.06, 0.03] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-[400px] h-[400px] rounded-full"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.1), transparent 70%)", top: "20%", right: "10%" }}
        animate={{ x: [0, -60, 0], y: [0, 40, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
};

export default MovingBackground;
